import React, { useRef, useState } from 'react';
import { css, cx, keyframes } from '@emotion/css';
import type { CSSInterpolation } from '@emotion/serialize';
import { useRenderContext } from '../../../context/RenderContext';
import type { ButtonVariant, ButtonSize } from '../../../styles/constants';
import { defaultProps as buttonDefaultProps } from '../../Button';
import { FORM_SUBMIT_BUTTON_NODE_NAME } from '../../../utils/constants';
import type { NodeCustomProps } from '../../../types/craft';
import { useEditor, useNode, type UserComponent, ROOT_NODE } from '@craftjs/core';
import { getBreakpointProp, setBreakpointProp } from '../../../utils/breakpointUtils';

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const LoadingIcon = () => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M13.5264 7.60596C13.5264 8.39389 13.3712 9.1741 13.0696 9.90206C12.7681 10.63 12.3262 11.2914 11.769 11.8486C11.2119 12.4057 10.5504 12.8477 9.82247 13.1492C9.09451 13.4508 8.3143 13.606 7.52637 13.606C6.73844 13.606 5.95822 13.4508 5.23027 13.1492C4.50231 12.8477 3.84088 12.4057 3.28373 11.8486C2.72657 11.2914 2.28462 10.63 1.98309 9.90206C1.68156 9.1741 1.52637 8.39389 1.52637 7.60596C1.52637 6.81802 1.68156 6.03781 1.98309 5.30986C2.28462 4.5819 2.72658 3.92047 3.28373 3.36332C3.84088 2.80616 4.50231 2.36421 5.23027 2.06268C5.95822 1.76115 6.73844 1.60596 7.52637 1.60596C8.3143 1.60596 9.09451 1.76115 9.82247 2.06268C10.5504 2.36421 11.2119 2.80617 11.769 3.36332C12.3262 3.92047 12.7681 4.5819 13.0696 5.30986C13.3712 6.03781 13.5264 6.81803 13.5264 7.60596L13.5264 7.60596Z"
      stroke="currentColor"
      strokeOpacity="0.3"
      strokeWidth="2"
    />
    <path
      d="M13.5264 7.60596C13.5264 8.23557 13.4273 8.86126 13.2327 9.46006"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const SuccessIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g clipPath="url(#clip0_success)">
      <path
        d="M8.00049 1.81307C11.9583 1.81334 15.1665 5.02219 15.1665 8.98006C15.1663 12.9378 11.9582 16.1458 8.00049 16.1461C4.04256 16.1461 0.833678 12.938 0.833496 8.98006C0.833496 5.02202 4.04245 1.81307 8.00049 1.81307ZM11.0933 6.46737C10.8104 6.23186 10.3904 6.27053 10.1548 6.55331L7.28857 9.99178L5.80518 8.50838C5.54483 8.24804 5.12217 8.24804 4.86182 8.50838C4.60163 8.76866 4.60173 9.19043 4.86182 9.45077L6.86182 11.4508C6.99426 11.5832 7.17665 11.6546 7.36377 11.6461C7.55067 11.6375 7.72536 11.5505 7.84521 11.4068L11.1792 7.40682C11.4149 7.12397 11.3761 6.70308 11.0933 6.46737Z"
        fill="currentColor"
      />
    </g>
    <defs>
      <clipPath id="clip0_success">
        <rect width="16" height="16" fill="currentColor" transform="translate(0 0.979736)" />
      </clipPath>
    </defs>
  </svg>
);

const ErrorIcon = () => (
  <svg
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.9801 3.47971C3.17536 3.28445 3.49194 3.28445 3.6872 3.47971L8.00032 7.79283L12.3134 3.47971C12.5087 3.28445 12.8253 3.28445 13.0205 3.47971C13.2158 3.67498 13.2158 3.99156 13.0205 4.18682L8.70742 8.49993L13.0205 12.813C13.2158 13.0083 13.2158 13.3249 13.0205 13.5202C12.8253 13.7154 12.5087 13.7154 12.3134 13.5202L8.00032 9.20704L3.6872 13.5202C3.49194 13.7154 3.17536 13.7154 2.9801 13.5202C2.78483 13.3249 2.78483 13.0083 2.9801 12.813L7.29321 8.49993L2.9801 4.18682C2.78483 3.99156 2.78483 3.67498 2.9801 3.47971Z"
      fill="currentColor"
    />
  </svg>
);

type FormSubmitButtonBaseProps = {
  text: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * When true, button expands to the full available width of the Form.
   * When false, it sizes to content.
   */
  fullWidth?: boolean;
};

type FormSubmitButtonBreakpointProps = Partial<FormSubmitButtonBaseProps>;

export type FormSubmitButtonProps = FormSubmitButtonBaseProps & {
  tablet?: FormSubmitButtonBreakpointProps;
  mobile?: FormSubmitButtonBreakpointProps;
};

const defaultProps: FormSubmitButtonProps = {
  text: 'Submit',
  // Source defaults from the shared Button component so Button owns variant/size
  variant: buttonDefaultProps.buttonVariant,
  size: buttonDefaultProps.buttonSize,
  fullWidth: true,
};

const FormSubmitButton: UserComponent<Partial<FormSubmitButtonProps>> = (incoming) => {
  const mergedProps: FormSubmitButtonProps = {
    ...defaultProps,
    ...incoming,
  } as FormSubmitButtonProps;

  const { isEditable, environment, buttonVariants } = useRenderContext();
  const [textEditingMode, setTextEditingMode] = useState(false);

  const {
    connectors: { connect, drag },
    actions: { setProp },
    isLocked,
    id,
  } = useNode((node) => ({
    isLocked: (node.data.custom as unknown as { isLocked?: boolean })?.isLocked,
    id: node.id,
  }));
  const { actions: editorActions, breakpoint } = useEditor((_, query) => {
    const rootNode = query.node(ROOT_NODE).get();
    return {
      breakpoint: rootNode?.data.props.breakpoint || 'desktop',
    };
  });

  // Read props using breakpoint-aware getter
  const text = getBreakpointProp(mergedProps, 'text', breakpoint);
  const variant = getBreakpointProp(mergedProps, 'variant', breakpoint);
  const size = getBreakpointProp(mergedProps, 'size', breakpoint);
  const fullWidth = getBreakpointProp(mergedProps, 'fullWidth', breakpoint);

  const editableSpanRef = useRef<HTMLSpanElement>(null);

  const setCursorToEnd = (element: HTMLElement) => {
    const range = globalThis.document.createRange();
    const selection = globalThis.window.getSelection();
    range.selectNodeContents(element);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  };

  const handleEnterEditingMode = () => {
    if (isEditable && !isLocked && !textEditingMode) {
      setTextEditingMode(true);
      if (editableSpanRef.current) {
        const el = editableSpanRef.current;
        setTimeout(() => {
          el.focus();
          setCursorToEnd(el);
        }, 0);
      }
    }
  };

  const commitText = (value: string) => {
    setProp((draft: FormSubmitButtonProps) => {
      setBreakpointProp(draft, 'text', value || 'Submit', breakpoint, FORM_SUBMIT_BUTTON_NODE_NAME);
    });
    setTextEditingMode(false);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLSpanElement> = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const target = e.target as HTMLSpanElement;
      commitText(target.textContent || target.innerHTML || '');
    }
  };

  const handleBlur: React.FocusEventHandler<HTMLSpanElement> = (e) => {
    const target = e.target as HTMLSpanElement;
    commitText(target.textContent || target.innerHTML || '');
  };

  const btnClass = buttonVariants({ variant, size });

  const disablePointerEvents = isEditable && isLocked;

  const extraClass = css({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: fullWidth ? '100%' : 'auto',
    alignSelf: fullWidth ? 'stretch' : 'auto',
    pointerEvents: disablePointerEvents ? 'none' : 'auto',
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed !important',
    },
    '&:not(:disabled)': {
      cursor: 'pointer',
    },
    // Icon containers - hidden by default
    '.button-icon': {
      display: 'none',
      position: 'absolute',
      alignItems: 'center',
      justifyContent: 'center',
    },
    // Loading state
    '&.form-button-is-loading': {
      '.button-text': { visibility: 'hidden' },
      '.button-icon-loading': {
        display: 'flex',
        animation: `${spin} 1s linear infinite`,
      },
    },
    // Success state
    '&.form-button-is-success': {
      '.button-text': { visibility: 'hidden' },
      '.button-icon-success': { display: 'flex' },
    },
    // Error state
    '&.form-button-is-error': {
      '.button-text': { visibility: 'hidden' },
      '.button-icon-error': { display: 'flex' },
    },
  } as CSSInterpolation);

  return (
    <div
      ref={(ref) => {
        if (ref && environment !== 'server') connect(drag(ref));
      }}
      onMouseDownCapture={(e) => {
        // Capture phase: stop parent Form selection and pick this node
        if (environment !== 'server' && isEditable && !isLocked) {
          e.stopPropagation();
          const ne = e.nativeEvent;
          if (ne && typeof ne.stopImmediatePropagation === 'function') {
            // Prevent Craft's parent listeners from running
            ne.stopImmediatePropagation();
          }
          editorActions.selectNode(id as string);
        }
      }}
      onMouseDown={(e) => {
        // Ensure this node is selected on click and not the parent Form
        if (environment !== 'server' && isEditable && !isLocked) {
          e.stopPropagation();
          editorActions.selectNode(id as string);
        }
      }}
      className={cx(
        'form-button-node',
        'boundary-indicator',
        // Match Field dashed/selection treatment
        isEditable && 'node-dashed-indicator',
      )}
    >
      <button
        type={environment !== 'server' ? 'button' : 'submit'}
        className={cx(btnClass, extraClass)}
        style={{ cursor: textEditingMode ? 'text' : 'pointer' }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDoubleClick={handleEnterEditingMode}
        data-role="form-submit"
      >
        <span
          ref={editableSpanRef}
          contentEditable={textEditingMode}
          suppressContentEditableWarning
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={cx('button-text', css({ outline: 'none' }))}
        >
          {text || 'Submit'}
        </span>
        <span className="button-icon button-icon-loading">
          <LoadingIcon />
        </span>
        <span className="button-icon button-icon-success">
          <SuccessIcon />
        </span>
        <span className="button-icon button-icon-error">
          <ErrorIcon />
        </span>
      </button>
    </div>
  );
};

FormSubmitButton.craft = {
  displayName: FORM_SUBMIT_BUTTON_NODE_NAME,
  props: defaultProps,
  custom: {
    displayName: 'Submit',
    isLocked: false,
  } as NodeCustomProps,
};

export default FormSubmitButton;
